import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import type { Sequence } from "@/lib/types";
import {
	ArrowLeft,
	Mail,
	Linkedin,
	Smartphone,
	Clock,
	Play,
	Pause,
	Users,
	Send,
	Eye,
	MessageSquare,
	Plus,
} from "lucide-react";

interface SequenceDetailProps {
	sequence: Sequence;
	onBack: () => void;
}

const channelConfig: Record<string, { icon: React.ReactNode; label: string; color: string; bg: string }> = {
	email: { icon: <Mail className="size-4" />, label: "Email", color: "text-neon-cyan", bg: "bg-neon-cyan/10 border-neon-cyan/20" },
	linkedin: { icon: <Linkedin className="size-4" />, label: "LinkedIn", color: "text-neon-blue", bg: "bg-neon-blue/10 border-neon-blue/20" },
	sms: { icon: <Smartphone className="size-4" />, label: "SMS", color: "text-neon-green", bg: "bg-neon-green/10 border-neon-green/20" },
};

const statusConfig: Record<string, { label: string; className: string }> = {
	active: { label: "Active", className: "bg-neon-green/15 text-neon-green border-neon-green/30" },
	paused: { label: "Paused", className: "bg-neon-purple/15 text-neon-purple border-neon-purple/30" },
	draft: { label: "Draft", className: "bg-muted text-muted-foreground border-border" },
};

export function SequenceDetail({ sequence, onBack }: SequenceDetailProps) {
	const status = statusConfig[sequence.status];
	const replyRate =
		sequence.metrics.sent > 0
			? ((sequence.metrics.replied / sequence.metrics.sent) * 100).toFixed(1)
			: "0";

	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Button variant="ghost" size="icon" onClick={onBack} className="shrink-0">
					<ArrowLeft className="size-4" />
				</Button>
				<div className="flex-1">
					<div className="flex items-center gap-3">
						<h1 className="text-2xl font-bold">{sequence.name}</h1>
						<Badge variant="outline" className={status.className}>
							{status.label}
						</Badge>
					</div>
					<p className="text-muted-foreground text-sm mt-1">{sequence.description}</p>
				</div>
				<Button
					variant="outline"
					className={
						sequence.status === "active"
							? "border-neon-purple/30 text-neon-purple hover:bg-neon-purple/10"
							: "border-neon-green/30 text-neon-green hover:bg-neon-green/10"
					}
				>
					{sequence.status === "active" ? (
						<>
							<Pause className="size-4 mr-2" /> Pause
						</>
					) : (
						<>
							<Play className="size-4 mr-2" /> Activate
						</>
					)}
				</Button>
			</div>

			{/* Metrics */}
			<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
				{[
					{ label: "Sent", value: sequence.metrics.sent, icon: Send, color: "text-neon-cyan" },
					{ label: "Opened", value: sequence.metrics.opened, icon: Eye, color: "text-neon-purple" },
					{ label: "Replied", value: sequence.metrics.replied, icon: MessageSquare, color: "text-neon-green" },
					{ label: "Contacts", value: sequence.contactCount, icon: Users, color: "text-neon-blue" },
				].map((stat) => (
					<Card key={stat.label} className="border-border/50">
						<CardContent className="py-3 px-4 flex items-center gap-3">
							<stat.icon className={`size-4 ${stat.color}`} />
							<div>
								<p className="text-xs text-muted-foreground">{stat.label}</p>
								<p className="text-lg font-bold">{stat.value.toLocaleString()}</p>
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{/* Reply Rate Banner */}
			<Card className="border-neon-cyan/20">
				<CardContent className="py-3 px-5 flex items-center justify-between">
					<span className="text-sm text-muted-foreground">Overall Reply Rate</span>
					<span className="text-2xl font-bold text-neon-green">{replyRate}%</span>
				</CardContent>
			</Card>

			{/* Steps Timeline */}
			<div>
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-lg font-semibold">Sequence Steps</h2>
					<Button variant="outline" size="sm" className="border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10">
						<Plus className="size-3.5 mr-1.5" /> Add Step
					</Button>
				</div>

				<div className="relative">
					{/* Timeline Line */}
					<div className="absolute left-[23px] top-6 bottom-6 w-px bg-gradient-to-b from-neon-cyan via-neon-purple to-neon-green opacity-30" />

					<div className="space-y-4">
						{sequence.steps.map((step, i) => {
							const ch = channelConfig[step.channel];
							return (
								<div key={step.id} className="relative flex gap-4">
									{/* Timeline Dot */}
									<div
										className={`size-12 rounded-xl border ${ch.bg} flex items-center justify-center shrink-0 z-10 ${ch.color}`}
									>
										{ch.icon}
									</div>

									{/* Step Card */}
									<Card className="flex-1 border-border/50 hover:border-border transition-colors">
										<CardContent className="py-3 px-4">
											<div className="flex items-center gap-2 mb-2">
												<Badge variant="secondary" className="text-xs">
													Step {step.order}
												</Badge>
												<Badge variant="outline" className={`text-xs ${ch.color}`}>
													{ch.label}
												</Badge>
												{step.delayDays > 0 && (
													<div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
														<Clock className="size-3" />
														{step.delayDays}d delay
													</div>
												)}
											</div>
											{step.subject && (
												<p className="text-sm font-medium mb-1">{step.subject}</p>
											)}
											<p className="text-sm text-muted-foreground whitespace-pre-line line-clamp-3">
												{step.body}
											</p>
										</CardContent>
									</Card>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}
